import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import CaseTimeline from './components/CaseTimeline';
import DocumentAccess from './components/DocumentAccess';
import CommunicationCenter from './components/CommunicationCenter';
import InvoiceViewer from './components/InvoiceViewer';
import NotificationSettings from './components/NotificationSettings';
import { useAuth } from 'contexts/AuthContext';
import clientPortalService from 'services/clientPortalService';

const ClientPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [language, setLanguage] = useState('en');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  
  // Real-time data states
  const [clientData, setClientData] = useState(null);
  const [activeCases, setActiveCases] = useState([]);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'Home', labelSw: 'Dashibodi' },
    { id: 'cases', label: 'My Cases', icon: 'Briefcase', labelSw: 'Kesi Zangu' },
    { id: 'documents', label: 'Documents', icon: 'FileText', labelSw: 'Hati' },
    { id: 'communication', label: 'Messages', icon: 'MessageSquare', labelSw: 'Ujumbe' },
    { id: 'invoices', label: 'Invoices', icon: 'Receipt', labelSw: 'Bili' },
    { id: 'settings', label: 'Settings', icon: 'Settings', labelSw: 'Mipangilio' }
  ];

  const translations = {
    en: {
      welcome: "Welcome back",
      activeCases: "Active Cases",
      recentUpdates: "Recent Updates",
      notifications: "Notifications",
      viewAll: "View All",
      nextCourtDate: "Next Court Date",
      progress: "Progress",
      lastUpdate: "Last Update",
      noCourtDate: "No court date scheduled",
      markAllRead: "Mark all as read",
      logout: "Logout"
    },
    sw: {
      welcome: "Karibu tena",
      activeCases: "Kesi za Sasa",
      recentUpdates: "Masasisho ya Hivi Karibuni",
      notifications: "Arifa",
      viewAll: "Ona Zote",
      nextCourtDate: "Tarehe ya Mahakama Ijayo",
      progress: "Maendeleo",
      lastUpdate: "Sasisha la Mwisho",
      noCourtDate: "Hakuna tarehe ya mahakama",
      markAllRead: "Weka zote kama zimesomwa",
      logout: "Toka"
    }
  };

  const t = translations?.[language];

  useEffect(() => {
    // Show welcome toast if arriving from profile setup
    if (location?.state?.fromSetup) {
      setShowWelcomeToast(true);
      const timer = setTimeout(() => setShowWelcomeToast(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [location?.state]);

  useEffect(() => {
    // Fade-in entrance animation
    const timer = setTimeout(() => setIsEntering(false), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        setError(null);

        // Fetch client profile
        const profileData = await clientPortalService?.getClientProfile(user?.id);
        if (profileData) {
          setClientData({
            name: profileData?.fullName || profile?.fullName || 'Client',
            email: profileData?.email || user?.email,
            phone: profileData?.phone || 'N/A',
            clientId: profileData?.id?.substring(0, 8)?.toUpperCase() || 'N/A',
            profileImage: profileData?.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
          });
        }

        // Fetch active cases
        const casesData = await clientPortalService?.getActiveCases(user?.id);
        const formattedCases = casesData?.map(case_ => ({
          id: case_?.id,
          title: case_?.caseTitle || 'Untitled Case',
          status: case_?.assignmentStatus === 'in_progress' ? 'Active' : 'Under Review',
          nextCourtDate: null,
          lawyer: case_?.assignedLawyer?.fullName || 'Not Assigned',
          progress: case_?.assignmentStatus === 'in_progress' ? 50 : 30,
          lastUpdate: `Updated ${new Date(case_?.updatedAt)?.toLocaleDateString()}`
        })) || [];
        setActiveCases(formattedCases);

        // Fetch notifications
        const notificationsData = await clientPortalService?.getNotifications(user?.id, 5);
        const formattedNotifications = notificationsData?.map(notif => ({
          id: notif?.id,
          type: notif?.notificationType,
          title: notif?.title,
          message: notif?.message,
          timestamp: getRelativeTime(notif?.createdAt),
          isRead: notif?.isRead
        })) || [];
        setRecentNotifications(formattedNotifications);

      } catch (err) {
        console.error('Error fetching client portal data:', err);
        setError(err?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();

    // Setup real-time subscriptions
    if (user?.id) {
      const unsubscribe = clientPortalService?.subscribeToUpdates(user?.id, {
        onCaseUpdate: (payload) => {
          console.log('Case updated:', payload);
          // Refresh cases
          clientPortalService?.getActiveCases(user?.id)?.then(casesData => {
            const formattedCases = casesData?.map(case_ => ({
              id: case_?.id,
              title: case_?.caseTitle || 'Untitled Case',
              status: case_?.assignmentStatus === 'in_progress' ? 'Active' : 'Under Review',
              nextCourtDate: null,
              lawyer: case_?.assignedLawyer?.fullName || 'Not Assigned',
              progress: case_?.assignmentStatus === 'in_progress' ? 50 : 30,
              lastUpdate: `Updated ${new Date(case_?.updatedAt)?.toLocaleDateString()}`
            })) || [];
            setActiveCases(formattedCases);
          });
        },
        onNotificationUpdate: (payload) => {
          console.log('New notification:', payload);
          // Add new notification to the list
          setRecentNotifications(prev => [
            {
              id: payload?.new?.id,
              type: payload?.new?.notificationType,
              title: payload?.new?.title,
              message: payload?.new?.message,
              timestamp: 'Just now',
              isRead: false
            },
            ...prev
          ]?.slice(0, 5));
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [user?.id, profile?.fullName]);

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    navigate('/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-success-100 text-success-700';
      case 'Under Review':
        return 'bg-warning-100 text-warning-700';
      case 'Completed':
        return 'bg-secondary-100 text-secondary-700';
      default:
        return 'bg-secondary-100 text-secondary-700';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'court_date':
        return 'Calendar';
      case 'document':
        return 'FileText';
      case 'invoice':
        return 'Receipt';
      default:
        return 'Bell';
    }
  };

  const renderDashboard = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-error-50 border border-error-200 rounded-lg p-6 text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto text-error-500 mb-4" />
          <p className="text-error-700 font-medium mb-2">Failed to load dashboard data</p>
          <p className="text-error-600 text-sm">{error}</p>
        </div>
      );
    }

    if (!clientData) {
      return (
        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6 text-center">
          <p className="text-text-secondary">No client data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary to-primary-600 rounded-lg p-6 text-white">
          <h1 className="text-2xl font-heading font-bold mb-2">{t?.welcome}, {clientData?.name?.split(' ')?.[0]}!</h1>
          <p className="opacity-90">Stay updated with your legal matters and case progress</p>
        </div>
        {/* Active Cases */}
        <div className="bg-surface rounded-lg shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold text-text-primary">{t?.activeCases}</h2>
            <button 
              onClick={() => setActiveTab('cases')}
              className="text-primary hover:text-primary-700 font-medium text-sm transition-smooth"
            >
              {t?.viewAll}
            </button>
          </div>
          {activeCases?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Briefcase" size={48} className="mx-auto text-text-secondary mb-4" />
              <p className="text-text-secondary">No active cases found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeCases?.map((case_) => (
                <div key={case_?.id} className="border border-border rounded-lg p-4 hover:shadow-card transition-smooth">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-text-primary">{case_?.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(case_?.status)}`}>
                          {case_?.status}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary mb-2">Case ID: {case_?.id}</p>
                      <p className="text-sm text-text-secondary">Lawyer: {case_?.lawyer}</p>
                    </div>
                    <div className="flex flex-col sm:items-end gap-2">
                      <div className="text-sm">
                        <span className="text-text-secondary">{t?.nextCourtDate}: </span>
                        <span className="font-medium text-text-primary">
                          {case_?.nextCourtDate || t?.noCourtDate}
                        </span>
                      </div>
                      <div className="w-full sm:w-32">
                        <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
                          <span>{t?.progress}</span>
                          <span>{case_?.progress}%</span>
                        </div>
                        <div className="w-full bg-secondary-200 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-smooth" 
                            style={{ width: `${case_?.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-sm text-text-secondary">
                      <span className="font-medium">{t?.lastUpdate}: </span>
                      {case_?.lastUpdate}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Recent Notifications */}
        <div className="bg-surface rounded-lg shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold text-text-primary">{t?.notifications}</h2>
            <button className="text-primary hover:text-primary-700 font-medium text-sm transition-smooth">
              {t?.markAllRead}
            </button>
          </div>
          {recentNotifications?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Bell" size={48} className="mx-auto text-text-secondary mb-4" />
              <p className="text-text-secondary">No notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentNotifications?.slice(0, 3)?.map((notification) => (
                <div 
                  key={notification?.id} 
                  className={`flex items-start gap-3 p-3 rounded-lg transition-smooth ${
                    notification?.isRead ? 'bg-secondary-50' : 'bg-primary-50 border border-primary-100'
                  }`}
                >
                  <div className={`p-2 rounded-full ${notification?.isRead ? 'bg-secondary-200' : 'bg-primary-100'}`}>
                    <Icon 
                      name={getNotificationIcon(notification?.type)} 
                      size={16} 
                      className={notification?.isRead ? 'text-secondary-600' : 'text-primary-600'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-text-primary text-sm">{notification?.title}</h4>
                    <p className="text-sm text-text-secondary mt-1">{notification?.message}</p>
                    <p className="text-xs text-text-secondary mt-2">{notification?.timestamp}</p>
                  </div>
                  {!notification?.isRead && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'cases':
        return <CaseTimeline cases={activeCases} language={language} userId={user?.id} />;
      case 'documents':
        return <DocumentAccess language={language} userId={user?.id} />;
      case 'communication':
        return <CommunicationCenter language={language} userId={user?.id} />;
      case 'invoices':
        return <InvoiceViewer language={language} userId={user?.id} />;
      case 'settings':
        return <NotificationSettings language={language} setLanguage={setLanguage} />;
      default:
        return renderDashboard();
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      style={{
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        opacity: isEntering ? 0 : 1,
        transform: isEntering ? 'translateY(16px)' : 'translateY(0)'
      }}
    >
      {/* Welcome Toast */}
      {showWelcomeToast && (
        <div
          className="fixed top-6 left-1/2 z-50 flex items-center gap-3 bg-white border border-success-200 shadow-xl rounded-xl px-5 py-4"
          style={{
            transform: 'translateX(-50%)',
            animation: 'toastSlideIn 0.4s ease forwards'
          }}
        >
          <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="CheckCircle" size={22} className="text-success-600" />
          </div>
          <div>
            <p className="font-semibold text-text-primary text-sm">Profile setup complete!</p>
            <p className="text-text-secondary text-xs mt-0.5">Welcome to your LawDesk KE Client Portal</p>
          </div>
          <button
            onClick={() => setShowWelcomeToast(false)}
            className="ml-2 text-text-secondary hover:text-text-primary transition-smooth"
          >
            <Icon name="X" size={16} />
          </button>
          <style>{`
            @keyframes toastSlideIn {
              from { opacity: 0; transform: translateX(-50%) translateY(-16px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}

      {/* Mobile Header */}
      <div className="lg:hidden bg-surface border-b border-border px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="p-2 rounded-lg hover:bg-secondary-50 transition-smooth"
        >
          <Icon name="Menu" size={24} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Scale" size={16} color="white" />
          </div>
          <span className="font-heading font-semibold text-primary">LawDesk KE</span>
        </div>
        <button
          onClick={() => setLanguage(language === 'en' ? 'sw' : 'en')}
          className="px-3 py-1 bg-secondary-100 rounded-full text-sm font-medium"
        >
          {language === 'en' ? 'SW' : 'EN'}
        </button>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      <div className="flex">
        {/* Sidebar */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-50 w-80 bg-surface border-r border-border transform transition-transform lg:transform-none
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={20} color="white" />
              </div>
              <div>
                <span className="text-lg font-heading font-semibold text-primary">LawDesk KE</span>
                <p className="text-xs text-text-secondary">Client Portal</p>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-secondary-50 transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Client Profile */}
          <div className="p-6 border-b border-border">
            {clientData ? (
              <div className="flex items-center gap-3">
                <Image
                  src={clientData?.profileImage}
                  alt={clientData?.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-text-primary truncate">{clientData?.name}</h3>
                  <p className="text-sm text-text-secondary truncate">{clientData?.email}</p>
                  <p className="text-xs text-text-secondary">ID: {clientData?.clientId}</p>
                </div>
              </div>
            ) : (
              <div className="animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-secondary-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-secondary-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {navigationItems?.map((item) => (
                <li key={item?.id}>
                  <button
                    onClick={() => {
                      setActiveTab(item?.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-smooth
                      ${activeTab === item?.id 
                        ? 'bg-primary-50 text-primary border-l-4 border-primary' :'text-text-secondary hover:bg-secondary-50 hover:text-text-primary'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-medium">
                      {language === 'en' ? item?.label : item?.labelSw}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-text-secondary">Language</span>
              <button
                onClick={() => setLanguage(language === 'en' ? 'sw' : 'en')}
                className="px-3 py-1 bg-secondary-100 rounded-full text-sm font-medium hover:bg-secondary-200 transition-smooth"
              >
                {language === 'en' ? 'English' : 'Kiswahili'}
              </button>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-error hover:bg-error-50 rounded-lg transition-smooth"
            >
              <Icon name="LogOut" size={20} />
              <span className="font-medium">{t?.logout}</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 lg:ml-0">
          <div className="p-4 lg:p-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPortal;